import React, { useState, useEffect, useRef } from 'react';
import {
    Search,
    User,
    FileText,
    Check,
    X,
    AlertCircle,
    Clock,
    DollarSign,
    FlaskConical,
    ClipboardList,
    Plus,
    Stethoscope,
    Upload,
    Gift,
    ChevronLeft,
    ChevronRight,
    Calendar,
    Syringe
} from 'lucide-react';
import SignaturePad from './SignaturePad';
import './StudyRegistration.css';

import { API_URL } from '../config/api';

const StudyRegistration = () => {
    const getLocalDateYMD = (date = new Date()) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };
    // Patient search state
    const [patientSearch, setPatientSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [searchingPatients, setSearchingPatients] = useState(false);

    // Pending studies from doctor requests
    const [pendingStudies, setPendingStudies] = useState([]);
    const [loadingPending, setLoadingPending] = useState(false);
    const [selectionMode, setSelectionMode] = useState('pending'); // 'pending' or 'catalog'

    // Study selection state
    const [studies, setStudies] = useState([]);
    const [categories, setCategories] = useState([]);
    const [studyFilter, setStudyFilter] = useState({ search: '', category: '' });
    const [selectedStudy, setSelectedStudy] = useState(null);
    const [selectedPendingStudy, setSelectedPendingStudy] = useState(null); // For studies already in DB
    const [loadingStudies, setLoadingStudies] = useState(true);
    const [doctors, setDoctors] = useState([]);
    const [requestingDoctor, setRequestingDoctor] = useState('');

    // Form state
    const [priority, setPriority] = useState('rutina');
    const [notes, setNotes] = useState('');
    const [isCostExempt, setIsCostExempt] = useState(false);
    const [signature, setSignature] = useState(null);
    const signatureRef = useRef(null);
    const folioRef = useRef(Date.now().toString().slice(-8));

    // UI state
    const [step, setStep] = useState(1); // 1: select patient, 2: select study, 3: authorization, 4: schedule
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [pdfFile, setPdfFile] = useState(null);
    const [createdStudyId, setCreatedStudyId] = useState(null);

    const getDoctorDisplayName = (doctor) => {
        if (!doctor) return 'SIN M\u00c9DICO';
        const fullName = [doctor.first_name, doctor.last_name, doctor.maternal_last_name]
            .filter(Boolean)
            .join(' ')
            .trim();
        return doctor.name || fullName || 'SIN M\u00c9DICO';
    };

    // Scheduling state (Step 4)
    const [scheduleAppointment, setScheduleAppointment] = useState(null); // null = not decided, true/false
    const [appointmentDate, setAppointmentDate] = useState('');
    const [appointmentTime, setAppointmentTime] = useState('');
    const [appointmentNotes, setAppointmentNotes] = useState('');

    // Fetch studies catalog on mount
    useEffect(() => {
        fetchStudies();
        fetchDoctors();
        // Fallback: If we had a previously logged requesting doctor from localStorage, we can initialize, 
        // but fetchDoctors will overwrite it if a match is found.
        const userRole = localStorage.getItem('userRole');
        const userId = localStorage.getItem('userId');
        if (userRole === 'medico' && userId) {
            setRequestingDoctor(userId);
        }
    }, []);

    // Search patients with debounce
    useEffect(() => {
        if (patientSearch.length < 2) {
            setSearchResults([]);
            return;
        }

        const timer = setTimeout(() => {
            searchPatients(patientSearch);
        }, 300);

        return () => clearTimeout(timer);
    }, [patientSearch]);

    const fetchStudies = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/catalog-studies?active=1&tipo_item=estudio`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const studiesList = Array.isArray(data) ? data : (data.studies || data.data || []);
                setStudies(studiesList);

                // Extract unique categories
                const uniqueCats = [...new Set(studiesList.map(s => s.category).filter(Boolean))];
                setCategories(uniqueCats);
            }
        } catch (err) {
            console.error('Error fetching studies:', err);
        } finally {
            setLoadingStudies(false);
        }
    };
    const fetchDoctors = async () => {
        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('userId');
            const userName = localStorage.getItem('userName');

            const res = await fetch(`${API_URL}/staff-doctors`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                const data = await res.json();
                // Add  option
                const doctorsList = [
                    { id: 0, name: 'SIN M\u00c9DICO' },
                    ...data
                ];
                setDoctors(doctorsList);

                if (userId) {
                    const loggedDoc = doctorsList.find(d =>
                        d.id.toString() === userId.toString() ||
                        (userName && d.name?.toLowerCase() === userName.toLowerCase()) ||
                        d.email === localStorage.getItem('userEmail')
                    );

                    if (loggedDoc) {
                        setRequestingDoctor(loggedDoc.id);
                    }
                }
            }
        } catch (err) {
            console.error('Error fetching doctors:', err);
        }
    };

    const fetchPendingStudies = async (patientId) => {
        try {
            setLoadingPending(true);
            const token = localStorage.getItem('token');
            // Get studies requested by doctor that are not yet activated
            const res = await fetch(`${API_URL}/patients/${patientId}/studies?status=solicitud_medico`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                const studiesList = Array.isArray(data) ? data : (data.studies || data.data || []);
                setPendingStudies(studiesList);
            }
        } catch (err) {
            console.error('Error fetching pending studies:', err);
        } finally {
            setLoadingPending(false);
        }
    };

    const searchPatients = async (term) => {
        try {
            setSearchingPatients(true);
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/patients?search=${encodeURIComponent(term)}&limit=10`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSearchResults(Array.isArray(data) ? data : (data.patients || data.data || []));
            }
        } catch (err) {
            console.error('Error searching patients:', err);
        } finally {
            setSearchingPatients(false);
        }
    };

    const selectPatient = (patient) => {
        setSelectedPatient(patient);
        setPatientSearch('');
        setSearchResults([]);
        fetchPendingStudies(patient.id);
        setSelectionMode('pending');
        setStep(2);
    };

    const selectStudy = (study) => {
        setSelectedStudy(study);
        setSelectedPendingStudy(null);
        setPriority(study.priority || 'rutina');
        setStep(3);
    };

    const selectPendingStudyItem = (pendingStudy) => {
        // Just select the study and show its details on Step 2
        // Activation will happen when user clicks "Siguiente"
        setSelectedPendingStudy(pendingStudy);
        setSelectedStudy(pendingStudy.catalog_study || {
            id: pendingStudy.catalog_study_id,
            name: pendingStudy.study_name || pendingStudy.name,
            code: pendingStudy.study_code,
            category: pendingStudy.category,
            processing_type: pendingStudy.processing_type,
            estimated_time: pendingStudy.estimated_time,
            price: pendingStudy.price,
            patient_instructions: pendingStudy.patient_instructions,
            requires_sample: pendingStudy.requires_sample
        });
        setPriority(pendingStudy.priority || 'rutina');
        setNotes(pendingStudy.special_notes || '');
        setIsCostExempt(!!pendingStudy.is_cost_exempt);
        if (pendingStudy.requested_by) {
            setRequestingDoctor(pendingStudy.requested_by);
        }
        setStep(3);
    };


    // Only validate and proceed to step 4 or finish
    const handleContinueToStep4 = async () => {
        if (submitting) return;
        if (!selectedPatient || !selectedStudy) {
            setError('Selecciona un paciente y un estudio');
            return;
        }

        if (!requestingDoctor && requestingDoctor !== 0) {
            setError('Debe seleccionar un  solicitante (o )');
            return;
        }

        // If it's a pending study, just proceed to step 4
        // Activation and consent will happen in handleFinalSubmit
        if (selectedPendingStudy) {
            setStep(4);
            return;
        }

        // For new studies, procedure as normal
        setError('');
        setStep(4);
    };

    // Update consent for existing pending study
    const handleUpdateExistingConsent = async () => {
        try {
            setSubmitting(true);
            setError('');
            const token = localStorage.getItem('token');

            const res = await fetch(`${API_URL}/patient-studies/${selectedPendingStudy.id}/consent`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    signature_data: signature,
                    consent_signed: !!signature,
                    consent_date: signature ? new Date().toISOString() : null,
                    special_notes: notes,
                    is_cost_exempt: isCostExempt,
                    requesting_doctor_id: (requestingDoctor !== "" && requestingDoctor !== null) ? requestingDoctor : null
                })
            });

            if (res.ok) {
                return true;
            } else {
                const data = await res.json();
                setError(data.message || 'Error al registrar consentimiento');
                return false;
            }
        } catch (err) {
            setError('Error de conexion');
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    // Create study + appointment (if scheduled) - CALLED ONLY AFTER USER CONFIRMS
    const handleFinalSubmit = async (scheduleType) => {
        if (submitting) return;
        // scheduleType: 'present' | 'scheduled'
        try {
            setSubmitting(true);
            setError('');
            const token = localStorage.getItem('token');

            // 1. CREATE THE STUDY (OR USE EXISTING)
            let studyId;

            if (selectedPendingStudy) {
                // Activate the study from solicitud_medico first
                const activateRes = await fetch(`${API_URL}/patient-studies/${selectedPendingStudy.id}/activate`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!activateRes.ok) {
                    const errData = await activateRes.json();
                    throw new Error(errData.message || 'Error al activar el estudio');
                }

                // Update consent for the pending study
                await handleUpdateExistingConsent();

                // Use the existing study id
                studyId = selectedPendingStudy.id;
            } else {
                // Create new study
                let studyRes;

                if (pdfFile) {
                    // Use FormData when file is present
                    const formData = new FormData();
                    formData.append('catalog_study_id', selectedStudy.id);
                    formData.append('priority', priority);
                    formData.append('special_notes', notes);
                    formData.append('is_cost_exempt', isCostExempt ? '1' : '0');
                    if (requestingDoctor !== "" && requestingDoctor !== null) formData.append('requesting_doctor_id', requestingDoctor);
                    formData.append('folio', folioRef.current);
                    formData.append('consent_pdf', pdfFile);

                    studyRes = await fetch(`${API_URL}/patients/${selectedPatient.id}/studies`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}` },
                        body: formData
                    });
                } else {
                    // Use JSON when no file
                    const payload = {
                        catalog_study_id: selectedStudy.id,
                        priority: priority,
                        special_notes: notes,
                        is_cost_exempt: isCostExempt,
                        requesting_doctor_id: (requestingDoctor !== "" && requestingDoctor !== null) ? requestingDoctor : null,
                        folio: folioRef.current
                    };

                    studyRes = await fetch(`${API_URL}/patients/${selectedPatient.id}/studies`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(payload)
                    });
                }

                if (!studyRes.ok) {
                    const errData = await studyRes.json();
                    throw new Error(errData.message || 'Error al registrar el estudio');
                }

                const responseData = await studyRes.json();
                studyId = responseData.data?.id || responseData.id || responseData.study_id;

                // Bug 3 fix: Activate the newly created study so it moves from
                // 'solicitud_medico' to the correct status (pendiente_muestra/solicitado)
                if (studyId) {
                    const activateRes = await fetch(`${API_URL}/patient-studies/${studyId}/activate`, {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    });

                    if (!activateRes.ok) {
                        console.warn('No se pudo activar el estudio automaticamente:', await activateRes.text());
                    }
                }
            }

            setCreatedStudyId(studyId);

            // 2. Si se agenda, primero crear cita y luego mover el estudio a cita_programada.
            if (scheduleType === 'scheduled') {
                const catalogStudyId = selectedStudy?.id || selectedPendingStudy?.catalog_study_id;
                const appointmentData = {
                    patient_id: selectedPatient.id,
                    patient_study_id: studyId,
                    appointment_date: appointmentDate,
                    appointment_time: appointmentTime,
                    notes: appointmentNotes,
                    study_ids: catalogStudyId ? [catalogStudyId] : []
                };

                const aptRes = await fetch(`${API_URL}/sample-appointments`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify(appointmentData)
                });

                if (!aptRes.ok) {
                    const aptErr = await aptRes.json();
                    throw new Error(
                        aptErr.message ||
                        'El estudio se registro, pero no se pudo crear la cita. Puedes reintentar en Citas de Muestras.'
                    );
                }

                if (studyId) {
                    const statusRes = await fetch(`${API_URL}/patient-studies/${studyId}/set-appointment-status`, {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    });
                    if (!statusRes.ok) {
                        throw new Error('La cita se creo, pero no se pudo actualizar el estatus del estudio.');
                    }
                }
            }

            setSuccess(true);
            setTimeout(() => resetForm(), 3000);

        } catch (err) {
            setError(err.message || 'Error de conexion');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle scheduling decision
    const handleScheduleDecision = async (shouldSchedule) => {
        if (submitting) return;
        if (!shouldSchedule) {
            // Patient is present - create study and send to nursing queue
            await handleFinalSubmit('present');
            return;
        }

        // Show scheduling form
        setScheduleAppointment(true);
        // Set default date to tomorrow
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setAppointmentDate(getLocalDateYMD(tomorrow));
        setAppointmentTime('08:00');
    };

    // Create the appointment
    const handleCreateAppointment = async () => {
        if (submitting) return;
        if (!appointmentDate || !appointmentTime) {
            setError('Selecciona fecha y hora');
            return;
        }

        // Create study + appointment together
        await handleFinalSubmit('scheduled');
    };

    const resetForm = () => {
        setSelectedPatient(null);
        setSelectedStudy(null);
        setSelectedPendingStudy(null);
        setPendingStudies([]);
        setSelectionMode('pending');
        setPatientSearch('');
        setPriority('rutina');
        setNotes('');
        setIsCostExempt(false);
        setSignature(null);
        setStep(1);
        setSuccess(false);
        setError('');
        setPdfFile(null);
        setCreatedStudyId(null);
        setScheduleAppointment(null);
        setAppointmentDate('');
        setAppointmentTime('');
        setAppointmentNotes('');

        // Reset doctor only if not logged as one
        const userRole = localStorage.getItem('userRole');
        if (userRole !== 'medico') {
            setRequestingDoctor('');
        }
        if (signatureRef.current) {
            signatureRef.current.clear();
        }
    };

    const goBack = () => {
        if (step === 4) {
            setScheduleAppointment(null);
            setStep(3);
        } else if (step === 3) {
            setSelectedStudy(null);
            setStep(2);
        } else if (step === 2) {
            setSelectedPatient(null);
            setStep(1);
        }
    };

    const filteredStudies = studies.filter(study => {
        const matchesSearch = !studyFilter.search ||
            study.name?.toLowerCase().includes(studyFilter.search.toLowerCase()) ||
            study.code?.toLowerCase().includes(studyFilter.search.toLowerCase());
        const matchesCategory = !studyFilter.category || study.category === studyFilter.category;
        return matchesSearch && matchesCategory;
    });

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatTime = () => {
        return new Date().toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const calculateAge = (birthDate) => {
        if (!birthDate) return '-';
        const today = new Date();
        const birth = new Date(birthDate);
        let age = today.getFullYear() - birth.getFullYear();
        const monthDiff = today.getMonth() - birth.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
            age--;
        }
        return `${age} `;
    };

    // Success screen
    if (success) {
        return (
            <div className="study-registration">
                <div className="success-screen">
                    <div className="success-icon">
                        <Check size={48} />
                    </div>
                    <h2></h2>
                    <p>El estudio ha sido dado de alta exitosamente.</p>
                    <p className="success-detail">
                        <strong>{selectedStudy?.name}</strong> para <strong>{selectedPatient?.first_name} {selectedPatient?.last_name} {selectedPatient?.maternal_last_name || ''}</strong>
                    </p>
                    <p className="success-info">El estudio  en la cola de  para toma de muestra.</p>
                    <button className="btn-primary" onClick={resetForm}>
                        Registrar Otro Estudio
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="study-registration">
            {/* Progress Steps */}
            <div className="registration-progress no-print">
                <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                    <div className="step-number">1</div>
                    <span>Paciente</span>
                </div>
                <div className="progress-line"></div>
                <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
                    <div className="step-number">2</div>
                    <span>Estudio</span>
                </div>
                <div className="progress-line"></div>
                <div className={`progress-step ${step >= 3 ? 'active' : ''} ${step > 3 ? 'completed' : ''}`}>
                    <div className="step-number">3</div>
                    <span>Autorización</span>
                </div>
                <div className="progress-line"></div>
                <div className={`progress-step ${step >= 4 ? 'active' : ''}`}>
                    <div className="step-number">4</div>
                    <span>Cita</span>
                </div>
            </div>

            {/* Step 1: Patient Selection */}
            {step === 1 && (
                <div className="step-content patient-step-container">
                    <div className="search-hero-section">
                        <div className="search-header">
                            <h2><User size={28} /> Seleccionar Paciente</h2>
                            <p>Busca un paciente existente por nombre, apellido o ID para comenzar el registro.</p>
                        </div>

                        <div className="search-box hero-search">
                            <input
                                type="text"
                                placeholder="Escribe el nombre del paciente..."
                                value={patientSearch}
                                onChange={(e) => setPatientSearch(e.target.value)}
                                autoFocus
                            />
                            {searchingPatients && <span className="searching-badge">Buscando...</span>}
                        </div>
                    </div>

                    {searchResults.length > 0 ? (
                        <div className="search-results animated-results">
                            <h3 className="results-label">Pacientes Encontrados ({searchResults.length})</h3>
                            {searchResults.map(patient => (
                                <div
                                    key={patient.id}
                                    className="patient-result-card"
                                    onClick={() => selectPatient(patient)}
                                >
                                    <div className="patient-avatar-large">
                                        {patient.first_name?.[0]}{patient.last_name?.[0]}
                                    </div>
                                    <div className="patient-info-large">
                                        <h4>{patient.first_name} {patient.last_name} {patient.maternal_last_name || ''}</h4>
                                        <div className="patient-meta">
                                            <span className="meta-item" title={patient.patient_code || 'N/A'}>
                                                <strong>ID:</strong> {patient.patient_code || 'N/A'}
                                            </span>
                                            <span className="meta-separator"></span>
                                            <span className="meta-item">
                                                <strong>Edad:</strong> {calculateAge(patient.date_of_birth)}
                                            </span>
                                            <span className="meta-separator"></span>
                                            <span className="meta-item" title={patient.email || 'Sin correo'}>
                                                {patient.email || 'Sin correo'}
                                            </span>
                                            <span className="meta-separator"></span>
                                            <span className="meta-item" title={patient.phone || 'Sin '}>
                                                <strong>Tel:</strong> {patient.phone || 'N/A'}
                                            </span>
                                            <span className="meta-separator"></span>
                                            <span className="meta-item">
                                                <strong>Exp:</strong> {patient.patient_code || 'S/E'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="select-action">
                                        <span className="btn-select">Seleccionar</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        patientSearch.length < 2 ? (
                            <div className="empty-search-state">
                                <div className="empty-icon-wrapper">
                                    <Search size={48} />
                                </div>
                                <h3>Buscar Paciente</h3>
                                <p>Ingresa al menos 2 caracteres para buscar en la base de datos.</p>
                            </div>
                        ) : !searchingPatients && (
                            <div className="no-results-state">
                                <AlertCircle size={48} />
                                <h3>No encontrado</h3>
                                <p>No se encontraron pacientes con "{patientSearch}".</p>
                            </div>
                        )
                    )}
                </div>
            )}

            {/* Step 2: Study Selection */}
            {step === 2 && (
                <div className="step-content">
                    <div className="step-header">
                        <button className="btn-back" onClick={goBack}>
                            <ChevronLeft size={18} />
                            Cambiar paciente
                        </button>
                        <div className="selected-patient-badge">
                            <User size={16} />
                            {selectedPatient?.first_name} {selectedPatient?.last_name} {selectedPatient?.maternal_last_name || ''}
                        </div>
                    </div>

                    {/* Mode Selector Tabs */}
                    <div className="mode-selector">
                        <button
                            className={`mode-tab ${selectionMode === 'pending' ? 'active' : ''}`}
                            onClick={() => setSelectionMode('pending')}
                        >
                            <Stethoscope size={20} />
                            Solicitados por
                            {pendingStudies.length > 0 && (
                                <span className="count-badge">{pendingStudies.length}</span>
                            )}
                        </button>
                        <button
                            className={`mode-tab ${selectionMode === 'catalog' ? 'active' : ''}`}
                            onClick={() => setSelectionMode('catalog')}
                        >
                            <FlaskConical size={20} />
                            de Estudios
                        </button>
                    </div>

                    {/* Pending Studies from Doctor */}
                    {selectionMode === 'pending' && (
                        <div className="pending-section">
                            <div className="section-intro">
                                <h3>Estudios Pendientes</h3>
                                <p>Selecciona los estudios solicitados por el  tratante.</p>
                            </div>

                            {loadingPending ? (
                                <p className="loading-text">Cargando estudios pendientes...</p>
                            ) : pendingStudies.length === 0 ? (
                                <div className="empty-pending">
                                    <div className="empty-icon-wrapper">
                                        <ClipboardList size={40} />
                                    </div>
                                    <h3>Todo </h3>
                                    <p>No hay estudios pendientes solicitados por el .</p>
                                    <button
                                        className="btn-primary"
                                        onClick={() => setSelectionMode('catalog')}
                                    >
                                        <Plus size={18} />
                                        Agregar Nuevo Estudio
                                    </button>
                                </div>
                            ) : (
                                <div className="pending-studies-list">
                                    {pendingStudies.map(study => (
                                        <div
                                            key={study.id}
                                            className="pending-study-card"
                                            onClick={() => selectPendingStudyItem(study)}
                                        >
                                            <div className="pending-study-header">
                                                <span className={`status-badge ${study.status}`}>
                                                    Solicitud Médica
                                                </span>
                                                {study.priority === 'urgente' && (
                                                    <span className="priority-badge urgente">URGENTE</span>
                                                )}
                                            </div>
                                            <h4>{study.study_name || study.catalog_study?.name}</h4>
                                            <div className="pending-study-info">
                                                <span><Clock size={14} /> {formatDate(study.requested_at)}</span>
                                                {study.doctor_name ? (
                                                    <span><User size={14} /> Dr. {study.doctor_name}</span>
                                                ) : study.requested_by && (
                                                    <span><User size={14} /> Dr. {study.requested_by}</span>
                                                )}
                                            </div>
                                            <button className="btn-select-pending">
                                                Registrar Estudio
                                                <ChevronRight size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Catalog Studies (New Study) */}
                    {selectionMode === 'catalog' && (
                        <div className="catalog-section">
                            <div className="catalog-search-hero">
                                <div className="search-box">
                                    <input
                                        type="text"
                                        placeholder="Buscar estudio por nombre,  o ..."
                                        value={studyFilter.search}
                                        onChange={(e) => setStudyFilter({ ...studyFilter, search: e.target.value })}
                                        autoFocus
                                    />
                                </div>
                                <div className="category-filter-wrapper">
                                    <select
                                        className="category-select"
                                        value={studyFilter.category}
                                        onChange={(e) => setStudyFilter({ ...studyFilter, category: e.target.value })}
                                    >
                                        <option value="">Todas las </option>
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {loadingStudies ? (
                                <p className="loading-text">Cargando ...</p>
                            ) : (
                                <div className="studies-grid">
                                    {filteredStudies.map(study => (
                                        <div
                                            key={study.id}
                                            className="registration-study-card"
                                            onClick={() => selectStudy(study)}
                                            title="Click para seleccionar"
                                        >
                                            <div className="study-card-content">
                                                <div className="study-card-header">
                                                    <span className="study-code">{study.code || 'N/A'}</span>
                                                    <span className={`processing-badge ${study.processing_type}`}>
                                                        {study.processing_type === 'externo' ? 'Externo' : 'Interno'}
                                                    </span>
                                                </div>
                                                <h4>{study.name}</h4>
                                                <div className="study-meta">
                                                    {study.category && (
                                                        <div className="meta-icon category-pill">
                                                            {study.category}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="study-card-footer">
                                                <div className="price-tag">
                                                    <DollarSign size={16} strokeWidth={3} />
                                                    {parseFloat(study.price || 0).toFixed(2)}
                                                </div>
                                                {study.estimated_time && (
                                                    <div className="meta-icon" title="Tiempo estimado">
                                                        <Clock size={14} />
                                                        {study.estimated_time} days
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Step 3: Authorization Document */}
            {step === 3 && (
                <div className="step-content authorization-step">
                    <div className="step-header no-print">
                        <button className="btn-back" onClick={goBack}> Cambiar estudio</button>
                    </div>

                    {/* Authorization Document */}
                    <div className="authorization-document" id="authorization-document">
                        <div className="document-header">
                            <div className="clinic-info">
                                <h1></h1>
                                <p>Servicios de Laboratorio y </p>
                            </div>
                            <div className="document-title">
                                <h2> DE TOMA DE MUESTRA</h2>
                                <p>Folio: {folioRef.current}</p>
                            </div>
                        </div>

                        <div className="document-section">
                            <h3>DATOS DEL PACIENTE</h3>
                            <div className="info-grid">
                                <div className="info-item">
                                    <label>Nombre Completo:</label>
                                    <span>{selectedPatient?.first_name} {selectedPatient?.last_name} {selectedPatient?.maternal_last_name || ''}</span>
                                </div>
                                <div className="info-item">
                                    <label>:</label>
                                    <span>{selectedPatient?.identification_number || 'N/A'}</span>
                                </div>
                                <div className="info-item">
                                    <label>Fecha de Nacimiento:</label>
                                    <span>{selectedPatient?.date_of_birth ? formatDate(selectedPatient.date_of_birth) : 'N/A'}</span>
                                </div>
                                <div className="info-item">
                                    <label>Edad:</label>
                                    <span>{calculateAge(selectedPatient?.date_of_birth)}</span>
                                </div>
                                <div className="info-item">
                                    <label>:</label>
                                    <span>{selectedPatient?.phone || 'N/A'}</span>
                                </div>
                                <div className="info-item">
                                    <label>Email:</label>
                                    <span>{selectedPatient?.email || 'N/A'}</span>
                                </div>
                                <div className="info-item">
                                    <label>No. de Expediente:</label>
                                    <span>{selectedPatient?.patient_code || 'N/A'}</span>
                                </div>
                                <div className="info-item">
                                    <label> Preferente:</label>
                                    <span>{selectedPatient?.preferred_doctor || 'No especificado'}</span>
                                </div>
                            </div>
                        </div>

                        <div className="document-section">
                            <h3>ESTUDIO SOLICITADO</h3>
                            <div className="info-grid">
                                <div className="info-item full-width">
                                    <label>Nombre del Estudio:</label>
                                    <span className="study-name-value">{selectedStudy?.name}</span>
                                </div>
                                <div className="info-item">
                                    <label>:</label>
                                    <span>{selectedStudy?.code || 'N/A'}</span>
                                </div>
                                <div className="info-item">
                                    <label>:</label>
                                    <span>{selectedStudy?.category || 'General'}</span>
                                </div>
                                <div className="info-item">
                                    <label>Tipo de Procesamiento:</label>
                                    <span>{selectedStudy?.processing_type === 'externo' ? 'Laboratorio Externo' : 'Laboratorio Interno'}</span>
                                </div>
                                <div className="info-item">
                                    <label>Tiempo Estimado:</label>
                                    <span>{selectedStudy?.estimated_time || 'Variable'}</span>
                                </div>
                                <div className="info-item">
                                    <label>Prioridad:</label>
                                    <span className={`priority-value ${priority}`}>
                                        {priority === 'urgente' ? 'URGENTE' : 'Rutina'}
                                    </span>
                                </div>
                                <div className="info-item">
                                    <label>Costo:</label>
                                    <span>${selectedStudy?.price ? parseFloat(selectedStudy.price).toFixed(2) : '0.00'} MXN</span>
                                </div>
                            </div>

                            {selectedStudy?.patient_instructions && (
                                <div className="instructions-box">
                                    <h4>Instrucciones para el paciente:</h4>
                                    <p>{selectedStudy.patient_instructions}</p>
                                </div>
                            )}
                        </div>

                        <div className="document-section pdf-upload-section">
                            <h3>Cargar consentimiento</h3>
                            <div className="pdf-upload-container">
                                <input
                                    type="file"
                                    id="pdf-upload"
                                    accept=".pdf"
                                    onChange={(e) => setPdfFile(e.target.files[0])}
                                    style={{ display: 'none' }}
                                />
                                <label htmlFor="pdf-upload" className="btn-upload-pdf">
                                    <Upload size={18} />
                                    {pdfFile ? pdfFile.name : 'Cargar archivo PDF'}
                                </label>
                                {pdfFile && (
                                    <button
                                        type="button"
                                        className="btn-remove-pdf"
                                        onClick={() => setPdfFile(null)}
                                    >
                                        <X size={16} />
                                        Quitar
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Form Options (no print) */}
                    <div className="form-options no-print">
                        <div className="option-group">
                            <label><Stethoscope size={16} />  Solicitante:</label>
                            <div className="option-controls">
                                <select
                                    className={`doctor-select ${(error && !requestingDoctor && requestingDoctor !== 0) ? 'input-error' : ''}`}
                                    value={requestingDoctor}
                                    onChange={(e) => setRequestingDoctor(e.target.value)}
                                >
                                    <option value="" disabled>Seleccionar ...</option>
                                    {doctors.map(doc => (
                                        <option key={doc.id} value={doc.id}>
                                            {getDoctorDisplayName(doc)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="option-group">
                            <label>Prioridad:</label>
                            <div className="option-controls">
                                <button
                                    type="button"
                                    className={`priority-btn ${priority === 'rutina' ? 'active' : ''}`}
                                    onClick={() => setPriority('rutina')}
                                >
                                    Rutina
                                </button>
                                <button
                                    type="button"
                                    className={`priority-btn urgente ${priority === 'urgente' ? 'active' : ''}`}
                                    onClick={() => setPriority('urgente')}
                                >
                                    Urgente
                                </button>
                            </div>
                        </div>

                        {/* Cost Exemption Option */}
                        <div className="option-group exempt-option">
                            <div
                                className={`exempt-toggle ${isCostExempt ? 'active' : ''}`}
                                onClick={() => setIsCostExempt(!isCostExempt)}
                            >
                                <div className={`toggle-checkbox ${isCostExempt ? 'checked' : ''}`}>
                                    {isCostExempt && <Gift size={14} />}
                                </div>
                                <div className="toggle-text">
                                    <span className="toggle-title">Exentar costo del estudio</span>
                                    <span className="toggle-desc">
                                        {isCostExempt
                                            ? `Estudio sin costo (precio original: $${selectedStudy?.price ? parseFloat(selectedStudy.price).toFixed(2) : '0.00'})`
                                            : 'Marcar si el paciente  por este estudio'
                                        }
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="option-group">
                            <label>Notas adicionales:</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Observaciones especiales..."
                                rows={2}
                            />
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="action-buttons no-print">
                        {error && (
                            <div className="error-alert-inline">
                                <AlertCircle size={18} />
                                {error}
                            </div>
                        )}
                        <div className="button-row">
                            <button className="btn-cancel" onClick={resetForm}>
                                <X size={18} />
                                Cancelar
                            </button>
                            <button
                                className="btn-submit"
                                onClick={handleContinueToStep4}
                                disabled={submitting}
                            >
                                {submitting ? 'Procesando...' : (
                                    <>
                                        <ChevronRight size={18} />
                                        Siguiente
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 4: Schedule Appointment */}
            {step === 4 && (
                <div className="step-content schedule-step">
                    <div className="schedule-header">
                        <div className="schedule-icon">
                            <Syringe size={48} />
                        </div>
                        <h2>Confirmar Toma de Muestra</h2>
                        <p className="patient-badge">
                            <User size={16} />
                            {selectedPatient?.first_name} {selectedPatient?.last_name} {selectedPatient?.maternal_last_name || ''} - {selectedStudy?.name}
                        </p>
                    </div>

                    {scheduleAppointment === null ? (
                        <div className="schedule-question">
                            <h3>¿El paciente está presente para la toma de muestra?</h3>
                            <p>Si el paciente está aquí, el estudio se registrará y pasará a enfermería. De lo contrario, se registrará con cita programada.</p>

                            <div className="schedule-options">
                                <button
                                    className="btn-option present"
                                    onClick={() => handleScheduleDecision(false)}
                                    disabled={submitting}
                                >
                                    <Check size={24} />
                                    <span>Sí, está presente</span>
                                    <small>Ir a cola de enfermería</small>
                                </button>
                                <button
                                    className="btn-option schedule"
                                    onClick={() => handleScheduleDecision(true)}
                                    disabled={submitting}
                                >
                                    <Calendar size={24} />
                                    <span>No, agendar cita</span>
                                    <small>Programar fecha y hora</small>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="schedule-form">
                            <h3><Calendar size={20} /> Agendar Cita para Toma de Muestra</h3>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Fecha</label>
                                    <input
                                        type="date"
                                        value={appointmentDate}
                                        onChange={(e) => setAppointmentDate(e.target.value)}
                                        min={getLocalDateYMD()}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Hora</label>
                                    <input
                                        type="time"
                                        value={appointmentTime}
                                        onChange={(e) => setAppointmentTime(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Notas para la cita</label>
                                <textarea
                                    value={appointmentNotes}
                                    onChange={(e) => setAppointmentNotes(e.target.value)}
                                    placeholder="Ej: Ayuno de 12 horas, traer resultados anteriores..."
                                    rows={2}
                                />
                            </div>

                            <div className="schedule-actions">
                                <button
                                    className="btn-cancel"
                                    onClick={() => setScheduleAppointment(null)}
                                >
                                    <ChevronLeft size={18} />
                                    Volver
                                </button>
                                <button
                                    className="btn-submit"
                                    onClick={handleCreateAppointment}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Agendando...' : (
                                        <>
                                            <Calendar size={18} />
                                            Agendar Cita
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default StudyRegistration;


